// src/components/Navbar.jsx
// PROD VERSION — Tools, Markets, Technical, Watchlist, Backtest hidden from nav
// To re-enable: move items from HIDDEN_LINKS back to NAV_LINKS

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';

// ── Visible in navbar ──────────────────────────────────────────────────────
const NAV_LINKS = [
  { to: '/modules',  label: 'Modules', icon: '📚' },
  { to: '/tools',   label: 'Tools',   icon: '🧮' },
  { to: '/glossary', label: 'Glossary',icon: '📖' },
  { to: '/blog', label: 'Blog', icon: '📝' }
];

// ── Hidden from navbar (still accessible via URL for internal testing) ─────
// const HIDDEN_LINKS = [
//   { to: '/tools',     label: 'Tools',     icon: '🧮' },
//   { to: '/markets',   label: 'Markets',   icon: '📊' },
//   { to: '/technical', label: 'Technical', icon: '🔬' },
//   { to: '/watchlist', label: 'Watchlist', icon: '⭐' },
//   { to: '/backtest',  label: 'Backtest',  icon: '🧪' },
// ];

export default function Navbar() {
  const { pathname }              = useLocation();
  const navigate                  = useNavigate();
  const { user, isPaid, signOut } = useAuth();
  const [menuOpen,   setMenuOpen  ] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  async function handleSignOut() {
    setSigningOut(true);
    await signOut();
    navigate('/');
    setSigningOut(false);
    setMenuOpen(false);
  }

  const isActive = (to) => pathname === to || pathname.startsWith(to + '/');

  return (
    <>
      <style>{`
        .nav-link {
          font-size: 13px; padding: 7px 10px; border-radius: 8px;
          text-decoration: none; color: #9ca3af;
          transition: color 0.15s, background 0.15s; white-space: nowrap;
        }
        .nav-link:hover { color: #fff; background: rgba(255,255,255,0.06); }
        .nav-link.active { color: #10b981; background: rgba(16,185,129,0.1); }
        .nav-start-btn {
          font-size: 13px; padding: 7px 16px; border-radius: 8px;
          background: #10b981; color: #fff; text-decoration: none;
          font-weight: 500; white-space: nowrap; border: none;
          cursor: pointer; font-family: inherit; transition: background 0.15s;
        }
        .nav-start-btn:hover { background: #0d9268; }
        .nav-outline-btn {
          font-size: 13px; padding: 6px 14px; border-radius: 8px;
          background: transparent; color: #9ca3af; text-decoration: none;
          font-weight: 500; white-space: nowrap; border: 1px solid rgba(255,255,255,0.12);
          cursor: pointer; font-family: inherit; transition: all 0.15s;
        }
        .nav-outline-btn:hover { color: #fff; border-color: rgba(255,255,255,0.3); }
        .mobile-menu-btn {
          display: none; width: 36px; height: 36px;
          align-items: center; justify-content: center; border-radius: 8px;
          background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
          color: #9ca3af; cursor: pointer; font-size: 18px;
        }
        .nav-links-desktop { display: flex; align-items: center; gap: 2px; }
        .nav-mobile-menu   { display: none; flex-direction: column; padding: 8px 16px 16px; gap: 4px; border-top: 1px solid rgba(255,255,255,0.06); }
        .nav-mobile-menu.open { display: flex; }
        @media (max-width: 900px) {
          .mobile-menu-btn   { display: flex !important; }
          .nav-links-desktop { display: none !important; }
        }
      `}</style>

      <nav style={{
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        position: 'sticky', top: 0,
        backgroundColor: 'rgba(10,10,10,0.97)',
        zIndex: 100, backdropFilter: 'blur(10px)',
      }}>
        <div style={{
          maxWidth: 1200, margin: '0 auto', padding: '0 20px',
          height: 56, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 12,
        }}>

          {/* Logo */}
          <Link to="/" style={{ fontSize: 18, fontWeight: 700, color: '#fff', textDecoration: 'none', flexShrink: 0 }}>
            TheTrade<span style={{ color: '#10b981' }}>Class</span>
          </Link>

          {/* Desktop nav */}
          <div className="nav-links-desktop">
            {NAV_LINKS.map(link => (
              <Link key={link.to} to={link.to}
                className={`nav-link${isActive(link.to) ? ' active' : ''}`}>
                {link.label}
              </Link>
            ))}
          </div>

          {/* Auth buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {user ? (
              <>
                {isPaid && (
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, backgroundColor: 'rgba(234,179,8,0.1)', color: '#eab308', border: '1px solid rgba(234,179,8,0.3)', fontWeight: 600 }}>PRO</span>
                )}
                <Link to="/dashboard" className={`nav-link${isActive('/dashboard') ? ' active' : ''}`}>Dashboard</Link>
                <Link to="/account"   className={`nav-link${isActive('/account')   ? ' active' : ''}`}>Account</Link>
                <button onClick={handleSignOut} disabled={signingOut} className="nav-outline-btn">
                  {signingOut ? 'Signing out…' : 'Log out'}
                </button>
              </>
            ) : (
              <>
                <Link to="/login"  className="nav-outline-btn">Log in</Link>
                <Link to="/signup" className="nav-start-btn">Sign up free</Link>
              </>
            )}
            <button className="mobile-menu-btn" onClick={() => setMenuOpen(o => !o)}>
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`nav-mobile-menu${menuOpen ? ' open' : ''}`}>
          {NAV_LINKS.map(link => (
            <Link key={link.to} to={link.to} onClick={() => setMenuOpen(false)}
              style={{ fontSize: 14, padding: '10px 12px', borderRadius: 8, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, color: isActive(link.to) ? '#10b981' : '#9ca3af', backgroundColor: isActive(link.to) ? 'rgba(16,185,129,0.08)' : 'transparent' }}>
              <span>{link.icon}</span>{link.label}
            </Link>
          ))}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 8, paddingTop: 12 }}>
            {user ? (
              <>
                <div style={{ fontSize: 11, color: '#4b5563', padding: '0 12px 8px' }}>
                  {user.email} {isPaid && <span style={{ color: '#eab308' }}>· PRO</span>}
                </div>
                <Link to="/dashboard" onClick={() => setMenuOpen(false)} style={{ display: 'block', fontSize: 14, padding: '10px 12px', borderRadius: 8, textDecoration: 'none', color: '#9ca3af' }}>📊 Dashboard</Link>
                <Link to="/account"   onClick={() => setMenuOpen(false)} style={{ display: 'block', fontSize: 14, padding: '10px 12px', borderRadius: 8, textDecoration: 'none', color: '#9ca3af' }}>👤 Account</Link>
                <button onClick={handleSignOut} style={{ width: '100%', textAlign: 'left', fontSize: 14, padding: '10px 12px', borderRadius: 8, background: 'none', border: 'none', color: '#f23645', cursor: 'pointer' }}>← Log out</button>
              </>
            ) : (
              <div style={{ display: 'flex', gap: 8, padding: '0 4px' }}>
                <Link to="/login"  onClick={() => setMenuOpen(false)} style={{ flex: 1, textAlign: 'center', padding: 10, borderRadius: 8, textDecoration: 'none', color: '#9ca3af', border: '1px solid rgba(255,255,255,0.1)', fontSize: 13 }}>Log in</Link>
                <Link to="/signup" onClick={() => setMenuOpen(false)} style={{ flex: 1, textAlign: 'center', padding: 10, borderRadius: 8, textDecoration: 'none', color: '#fff', backgroundColor: '#10b981', fontSize: 13, fontWeight: 500 }}>Sign up</Link>
              </div>
            )}
          </div>
        </div>
      </nav>
    </>
  );
}